import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import logo from "./images/techvanto-logo.png";

const LOGO_SRC = logo;

// ── Nav Data ──────────────────────────────────────────────────────────────────
const NAV_LINKS = [
  {
    label: "Services",
    dropdown: [
      { name: "Web Development", link: "/web-development" },
      { name: "Mobile App Development", link: "/mobile-app-development" },
      { name: "AI & ML Solutions", link: "/ai-ml-solutions" },
      { name: "Cloud DevOps", link: "/cloud" },
      { name: "UI/UX Design", link: "/ui-ux" },
      { name: "E-Commerce Solutions", link: "/ecommerce" },
    ],
  },
  {
    label: "Solutions",
    dropdown: [
      { name: "CRM Solution", link: "/crm-solutions" },
      { name: "ERP Solution", link: "/erp-solutions" },
      { name: "HRM Solution", link: "/hrm-solutions" },
      { name: "LMS Solution", link: "/lms-solutions" },
      { name: "POS Solution", link: "/pos-solutions" },
      { name: "SaaS Platform", link: "/saas-platform" },
    ],
  },
  {
    label: "Industries",
      dropdown: [
      { name: "Healthcare", link: "/healthcare-solution" },
      { name: "FinTech", link: "/fintech-solution" },
      { name: "EdTech", link: "/edtech-solution" },
      { name: "Retail & E-Commerce", link: "retail-ecommerce-solution" },
      { name: "Real Estate", link: "/realestate-solution" },
      { name: "Logistics", link: "/logistic-solution" },
    ],
  },
  { label: "Portfolio", dropdown: null, link: "/portfolio" },
  { label: "About", dropdown: null, link: "/#about" },
  { label: "Blog", dropdown: null, link: "/blogs" },
];

// ── Page Data ─────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: "🏗️",
    title: "Custom POS Development",
    desc: "Fully bespoke POS systems engineered around your exact sales process — no forced workflows, no unused features. Built to scale with your team.",
    tags: ["React", "Node.js", "PostgreSQL", "Redis"],
    color: "#3B82F6",
  },
  {
    icon: "☁️",
    title: "Salesforce Customisation",
    desc: "Custom Salesforce objects, flows, Lightning components, AppExchange integrations, and full-org implementations tailored to your industry.",
    tags: ["Salesforce", "Apex", "LWC", "Flow Builder"],
    color: "#00A1E0",
  },
  {
    icon: "🔧",
    title: "Zoho POS Implementation",
    desc: "End-to-end Zoho POS setup, blueprint configuration, custom modules, and Zoho One ecosystem integrations for SMBs and enterprises alike.",
    tags: ["Zoho POS", "Zoho One", "Deluge", "Blueprints"],
    color: "#E42527",
  },
  {
    icon: "🤝",
    title: "POS Integration Services",
    desc: "Seamlessly connect your POS with ERP, marketing automation, accounting, e-commerce, and communication tools for a unified data ecosystem.",
    tags: ["REST APIs", "Webhooks", "Zapier", "MuleSoft"],
    color: "#10B981",
  },
  {
    icon: "📊",
    title: "Sales Pipeline Automation",
    desc: "Intelligent lead scoring, automated follow-up sequences, deal stage triggers, and AI-powered next-best-action recommendations that close more deals.",
    tags: ["Automation", "AI Scoring", "Workflows", "Sequences"],
    color: "#8B5CF6",
  },
  {
    icon: "📱",
    title: "Mobile POS Apps",
    desc: "Native iOS & Android POS apps that give your field sales team real-time access to customer data, meeting notes, and pipeline updates — anywhere.",
    tags: ["Flutter", "React Native", "Offline Sync", "GPS"],
    color: "#F59E0B",
  },
  {
    icon: "🤖",
    title: "AI-Powered POS",
    desc: "Churn prediction models, sentiment analysis on support tickets, conversation intelligence, and GPT-powered email drafting built into your POS.",
    tags: ["Python", "GPT-4", "ML", "NLP"],
    color: "#EC4899",
  },
  {
    icon: "🔄",
    title: "POS Migration & Cleanup",
    desc: "Pain-free migrations from legacy systems or spreadsheets — with data deduplication, enrichment, validation, and zero business disruption.",
    tags: ["ETL", "Data Cleansing", "Deduplication", "Mapping"],
    color: "#06B6D4",
  },
];

const TECH_STACK = [
  {
    category: "POS Platforms",
    items: [
      { name: "Salesforce", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/salesforce/salesforce-original.svg", color: "#00A1E0" },
      { name: "Zoho POS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#E42527" },
      { name: "HubSpot", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#FF7A59" },
      { name: "Microsoft POS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Pipedrive", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#26292C" },
      { name: "Freshsales", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#12B76A" },
    ],
  },
  {
    category: "Frontend",
    items: [
      { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg", color: "#61DAFB" },
      { name: "Next.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nextjs/nextjs-original.svg", color: "#000" },
      { name: "TypeScript", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/typescript/typescript-original.svg", color: "#3178C6" },
      { name: "Vue.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/vuejs/vuejs-original.svg", color: "#42B883" },
      { name: "Tailwind", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/tailwindcss/tailwindcss-original.svg", color: "#06B6D4" },
      { name: "Figma", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/figma/figma-original.svg", color: "#F24E1E" },
    ],
  },
  {
    category: "Backend & Database",
    items: [
      { name: "Node.js", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/nodejs/nodejs-original.svg", color: "#339933" },
      { name: "Python", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/python/python-original.svg", color: "#3776AB" },
      { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg", color: "#4169E1" },
      { name: "MongoDB", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/mongodb/mongodb-original.svg", color: "#47A248" },
      { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg", color: "#DC382D" },
      { name: "GraphQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/graphql/graphql-plain.svg", color: "#E10098" },
    ],
  },
  {
    category: "Cloud & DevOps",
    items: [
      { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-plain-wordmark.svg", color: "#FF9900" },
      { name: "Azure", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/azure/azure-original.svg", color: "#0078D4" },
      { name: "Docker", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/docker/docker-original.svg", color: "#2496ED" },
      { name: "Kubernetes", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/kubernetes/kubernetes-plain.svg", color: "#326CE5" },
      { name: "GitHub", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg", color: "#181717" },
      { name: "GCP", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/googlecloud/googlecloud-original.svg", color: "#4285F4" },
    ],
  },
];

const PROCESS = [
  { n: "01", icon: "🔍", title: "POS Audit & Strategy", desc: "We audit your current sales process, data landscape, team workflows, and technology stack to design a POS architecture that drives real adoption.", color: "#3B82F6" },
  { n: "02", icon: "🗺️", title: "Process Mapping & Design", desc: "Every sales stage, lead source, automation rule, and reporting dashboard is mapped before a single line of code is written — no surprises mid-project.", color: "#10B981" },
  { n: "03", icon: "⚙️", title: "Development & Configuration", desc: "Agile 2-week sprints with a live staging POS from day one. Your sales team reviews real workflows, not static mockups.", color: "#8B5CF6" },
  { n: "04", icon: "🔗", title: "Integrations & Data Migration", desc: "Connecting your POS to email, calendar, ERP, marketing tools, and migrating all historical data with full validation and zero data loss.", color: "#F59E0B" },
  { n: "05", icon: "🧪", title: "UAT & Sales Team Training", desc: "User acceptance testing with your actual sales reps, feedback-driven refinements, and hands-on training to ensure high adoption from day one.", color: "#EF4444" },
  { n: "06", icon: "📈", title: "Launch & Continuous Optimisation", desc: "Go-live support, adoption monitoring, monthly pipeline health reports, and iterative feature releases to keep your POS evolving with your business.", color: "#EC4899" },
];

const CASE_STUDIES = [
  {
    title: "GetMyProperty POS",
    industry: "Real Estate",
    desc: "A custom POS for a pan-India real estate group managing 500+ agents, 10,000+ leads per month, and automated follow-up sequences that reduced response time from 48 hours to under 5 minutes — tripling conversion rates.",
    img: "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=600&h=400&fit=crop&auto=format",
    tags: ["React", "Node.js", "PostgreSQL", "WhatsApp API"],
    metrics: [{ val: "3x", lbl: "Lead Conversion" }, { val: "5 min", lbl: "Avg Response Time" }, { val: "500+", lbl: "Agents Onboarded" }],
    color: "#3B82F6",
  },
  {
    title: "HealthTrack Patient POS",
    industry: "Healthcare",
    desc: "A HIPAA-compliant patient relationship management system for a 200-hospital network — featuring appointment scheduling, treatment follow-ups, insurance claim tracking, and patient satisfaction scoring.",
    img: "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop&auto=format",
    tags: ["Salesforce Health Cloud", "HIPAA", "HL7", "React"],
    metrics: [{ val: "200+", lbl: "Hospitals" }, { val: "40%", lbl: "Admin Time Saved" }, { val: "98%", lbl: "Patient Retention" }],
    color: "#10B981",
  },
  {
    title: "SSL Group B2B Sales POS",
    industry: "Industrial B2B",
    desc: "An enterprise POS replacing 12 spreadsheets for a pan-India industrial group — with territory management, distributor portals, order pipeline tracking, and deep SAP integration for 300+ field sales executives.",
    img: "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=600&h=400&fit=crop&auto=format",
    tags: ["Custom React POS", "SAP Integration", "Node.js", "Power BI"],
    metrics: [{ val: "300+", lbl: "Sales Executives" }, { val: "60%", lbl: "Reporting Time Saved" }, { val: "25%", lbl: "Revenue Growth" }],
    color: "#8B5CF6",
  },
];

const FAQS = [
  { q: "Should we build a custom POS or implement Salesforce / Zoho?", a: "It depends on your team size, budget, and process complexity. Off-the-shelf platforms like Salesforce or Zoho are ideal if your process fits within standard sales workflows and you want fast deployment. Custom POS development makes sense when you have unique business rules, need deep integration with proprietary systems, or find that platforms charge for features you'll never use. We'll recommend the right fit after a free 30-minute process audit." },
  { q: "How long does a POS implementation take?", a: "A Salesforce or Zoho POS configuration project takes 4–10 weeks depending on complexity. A mid-complexity custom POS with standard modules takes 3–5 months. A full enterprise POS with mobile apps, AI features, and ERP integrations typically takes 5–9 months. Timeline is primarily driven by the number of integrations and data migration complexity." },
  { q: "Can you migrate our data from spreadsheets or our old POS?", a: "Yes. We've migrated from Excel, Google Sheets, legacy ACT!, Sugar POS, and dozens of custom systems. Our ETL process includes data profiling, deduplication, field mapping, validation rules, and a dry-run migration in a sandbox before touching production data. You'll see exactly what's migrating before we go live." },
  { q: "How do you ensure our team actually adopts the POS?", a: "Adoption is our top metric — not just delivery. We involve your sales team early in UAT, build workflows around how they actually work (not how textbooks say they should), run role-specific training sessions, create video walkthroughs, and provide a 30-day hypercare support period. Our POS projects average 85%+ active user adoption within 60 days of go-live." },
  { q: "What integrations can you build for our POS?", a: "We've integrated POSs with Gmail/Outlook, WhatsApp Business API, Twilio SMS, Mailchimp/Klaviyo, QuickBooks/Tally, SAP, Oracle, Shopify, WooCommerce, Shiprocket, Zoom, Google Calendar, LinkedIn Sales Navigator, and many more. If it has an API, we can integrate it. We also build custom middleware for legacy systems without APIs." },
  { q: "Is our customer data secure in the POS?", a: "Security is non-negotiable. We implement role-based access control (RBAC), field-level security, IP whitelisting, two-factor authentication, full audit trails, AES-256 encryption at rest, and TLS 1.3 in transit. For Healthcare and FinTech clients, we apply additional HIPAA and PCI DSS controls. All data resides in your own cloud infrastructure — we never have access to your production data." },
];

const STATS = [
  { val: "180+", lbl: "POS Projects Delivered", icon: "🤝" },
  { val: "85%+", lbl: "Avg User Adoption Rate", icon: "📈" },
  { val: "3x", lbl: "Avg Sales Conversion Lift", icon: "💰" },
  { val: "40%", lbl: "Avg Admin Time Saved", icon: "⏱️" },
];

const POS_MODULES = [
  { icon: "👥", title: "Contact & Account Management", desc: "360° customer profiles with activity timelines", color: "#3B82F6" },
  { icon: "🎯", title: "Lead & Pipeline Management", desc: "Visual Kanban pipeline with drag-drop deal stages", color: "#10B981" },
  { icon: "⚡", title: "Sales Automation", desc: "Auto-assign, follow-up sequences, task triggers", color: "#8B5CF6" },
  { icon: "📧", title: "Email & WhatsApp Integration", desc: "Two-way email sync, WhatsApp Business API", color: "#F59E0B" },
  { icon: "📊", title: "Reports & Dashboards", desc: "Real-time sales analytics and forecasting", color: "#EF4444" },
  { icon: "🤖", title: "AI Lead Scoring", desc: "ML-powered propensity-to-buy scoring", color: "#EC4899" },
  { icon: "📱", title: "Mobile POS", desc: "Full-featured iOS & Android apps for field teams", color: "#06B6D4" },
  { icon: "🔗", title: "Third-Party Integrations", desc: "ERP, accounting, marketing, and communication tools", color: "#6366F1" },
];

// ── Navbar ────────────────────────────────────────────────────────────────────
function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Smart logic to check which page we are currently on
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '/';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
      background: scrolled ? "rgba(255,255,255,0.97)" : "rgba(255,255,255,0.9)",
      backdropFilter: "blur(16px)",
      borderBottom: scrolled ? "1px solid #E5E7EB" : "1px solid transparent",
      boxShadow: scrolled ? "0 2px 24px rgba(0,0,0,0.08)" : "none",
      transition: "all .3s ease",
    }}>
      <div style={{
        background: "linear-gradient(90deg,#1E3A8A,#3B82F6)",
        color: "#fff", fontSize: 12, padding: "6px 40px",
        display: "flex", justifyContent: "space-between", alignItems: "center",
        flexWrap: "wrap", gap: 8,
      }}>
        <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
          <span>📞 +91 96462 06032</span>
          <span>✉️ info@techvanto.com</span>
        </div>
        <span className="topbar-right" style={{ opacity: .85 }}>🌏 Trusted by 1500+ clients across 20+ countries</span>
      </div>
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "0 40px", height: 68, maxWidth: 1280, margin: "0 auto",
      }}>
        <a href="/" style={{ display: "flex", alignItems: "center", flexShrink: 0, textDecoration: "none" }}>
          <img src={LOGO_SRC} alt="Techvanto" style={{ height: 150, width: "auto", objectFit: "contain", display: "block" }} />
        </a>
        <div className="nav-links" style={{ alignItems: "center", gap: 4 }}>
          {NAV_LINKS.map(link => {
            // Check if this specific main link is the active page
            const isActive = link.link && currentPath.includes(link.label.toLowerCase());

            return (
              <div key={link.label} style={{ position: "relative" }}
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                {link.link ? (
                  <a href={link.link} style={{
                    background: isActive ? "#EFF6FF" : "none",
                    border: isActive ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: isActive ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s, background .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8, textDecoration: "none"
                  }}>
                    {link.label}
                  </a>
                ) : (
                  <button style={{
                    // Highlight the "Services" button if we are on the web-development page
                    background: (link.label === "Services" && currentPath === "/web-development") ? "#EFF6FF" : "none",
                    border: (link.label === "Services" && currentPath === "/web-development") ? "1px solid #BFDBFE" : "1px solid transparent",
                    cursor: "pointer", padding: "8px 14px", fontSize: 14, fontWeight: 600,
                    color: (link.label === "Services" && currentPath === "/web-development") ? "#3B82F6" : activeDropdown === link.label ? "#3B82F6" : "#374151",
                    display: "flex", alignItems: "center", gap: 4, transition: "color .2s",
                    fontFamily: "'Sora',sans-serif", borderRadius: 8,
                  }}>
                    {link.label}
                    {link.dropdown && <span style={{ fontSize: 10, transition: "transform .2s", transform: activeDropdown === link.label ? "rotate(180deg)" : "rotate(0)" }}>▾</span>}
                  </button>
                )}
                {link.dropdown && activeDropdown === link.label && (
                  <div style={{
                    position: "absolute", top: "100%", left: 0, zIndex: 200,
                    background: "#fff", border: "1px solid #E5E7EB", borderRadius: 12,
                    boxShadow: "0 16px 48px rgba(0,0,0,0.12)", minWidth: 220,
                    padding: "8px 0", animation: "fadeIn .2s ease",
                  }}>
                    {link.dropdown.map(item => (
                      <a key={item.name} href={item.link} style={{
                        display: "block", padding: "9px 20px", fontSize: 13.5,
                        // Highlight "Web Development" inside the dropdown specifically
                        color: currentPath === item.link ? "#3B82F6" : "#374151",
                        background: currentPath === item.link ? "#EFF6FF" : "none",
                        fontWeight: currentPath === item.link ? 700 : 500,
                        textDecoration: "none", transition: "all .15s",
                      }}
                        onMouseEnter={e => { e.target.style.background = "#EFF6FF"; e.target.style.color = "#3B82F6"; }}
                        onMouseLeave={e => { 
                          e.target.style.background = currentPath === item.link ? "#EFF6FF" : "none"; 
                          e.target.style.color = currentPath === item.link ? "#3B82F6" : "#374151"; 
                        }}
                      >{item.name}</a>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
          <a href="/#contact" className="nav-cta" style={{
            background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
            padding: "10px 22px", borderRadius: 8, fontSize: 13.5, fontWeight: 700,
            textDecoration: "none", boxShadow: "0 4px 14px rgba(59,130,246,.35)",
            transition: "transform .2s, box-shadow .2s", fontFamily: "'Sora',sans-serif",
          }}
            onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-1px)"; e.currentTarget.style.boxShadow = "0 8px 20px rgba(59,130,246,.45)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 4px 14px rgba(59,130,246,.35)"; }}
          >Get Free Consultation</a>
          <button onClick={() => setMobileOpen(!mobileOpen)} className="mob-menu-btn" style={{
            background: "none", border: "1px solid #E5E7EB", borderRadius: 8,
            padding: "8px 12px", cursor: "pointer", fontSize: 18, color: "#374151",
          }}>☰</button>
        </div>
      </div>
      {mobileOpen && (
        <div style={{ background: "#fff", borderTop: "1px solid #E5E7EB", padding: "16px 24px 24px", display: "flex", flexDirection: "column", gap: 4 }}>
          <div style={{ paddingBottom: 14, borderBottom: "1px solid #F3F4F6", marginBottom: 4 }}>
            <img src={LOGO_SRC} alt="Techvanto" style={{ height: 40, width: "auto", objectFit: "contain" }} />
          </div>
          {NAV_LINKS.map(l => (
            <a key={l.label} href={l.link || "#"} style={{ padding: "10px 0", color: currentPath.includes(l.label.toLowerCase()) ? "#3B82F6" : "#374151", fontWeight: 600, textDecoration: "none", borderBottom: "1px solid #F3F4F6", fontFamily: "'Sora',sans-serif" }}>{l.label}</a>
          ))}
          <a href="/#contact" style={{ marginTop: 12, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", padding: "13px", borderRadius: 8, textAlign: "center", fontWeight: 700, textDecoration: "none" }}>Get Free Consultation</a>
        </div>
      )}
    </nav>
  );
}

// ── Hero ──────────────────────────────────────────────────────────────────────
function Hero() {
  const [activeMetric, setActiveMetric] = useState(0);
  const metrics = ["3x More Deals Closed", "85% Team Adoption", "40% Less Admin Work", "360° Customer View"];

  useEffect(() => {
    const t = setInterval(() => setActiveMetric(p => (p + 1) % metrics.length), 2400);
    return () => clearInterval(t);
  }, []);

  return (
    <section style={{
      paddingTop: 148, paddingBottom: 96,
      background: "linear-gradient(160deg,#EFF6FF 0%,#F0FDF4 50%,#EFF6FF 100%)",
      position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: -80, right: -80, width: 600, height: 600, borderRadius: "50%", background: "radial-gradient(circle,rgba(59,130,246,.12),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: -80, left: -80, width: 500, height: 500, borderRadius: "50%", background: "radial-gradient(circle,rgba(16,185,129,.08),transparent 70%)", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", backgroundImage: "linear-gradient(rgba(59,130,246,.04) 1px,transparent 1px),linear-gradient(90deg,rgba(59,130,246,.04) 1px,transparent 1px)", backgroundSize: "60px 60px" }} />

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 40px", position: "relative" }}>
        {/* Breadcrumb */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 32, fontSize: 13, color: "#6B7280" }}>
          <Link to="/" style={{ color: "#3B82F6", textDecoration: "none", fontWeight: 600 }}>Home</Link>
          <span>›</span>
          <span>Solutions</span>
          <span>›</span>
          <span style={{ color: "#0F172A", fontWeight: 600 }}>POS Solution</span>
        </div>

        <div className="pos-hero-grid">
          {/* Left */}
          <div>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 99, padding: "6px 16px", marginBottom: 24 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#3B82F6", display: "inline-block", animation: "pulse 2s infinite" }} />
              <span style={{ fontSize: 13, fontWeight: 600, color: "#1E40AF" }}>🤝 India's Leading POS Development Company</span>
            </div>
            <h1 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(34px,4.5vw,58px)", fontWeight: 900, color: "#0F172A", lineHeight: 1.1, marginBottom: 20 }}>
              POS That Closes<br />
              <span style={{ background: "linear-gradient(135deg,#1E3A8A,#3B82F6,#10B981)", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", backgroundClip: "text" }}>
                More Deals
              </span>
            </h1>
            <p style={{ fontSize: 17, color: "#6B7280", lineHeight: 1.8, maxWidth: 560, marginBottom: 28 }}>
              We build POS systems that your sales team actually loves using — because we design around your process, not the other way around. From custom-built platforms to Salesforce and Zoho implementations.
            </p>

            {/* Live metric ticker */}
            <div style={{ display: "inline-flex", alignItems: "center", gap: 10, background: "#fff", border: "1.5px solid #BFDBFE", borderRadius: 10, padding: "10px 18px", marginBottom: 36 }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#10B981", display: "inline-block", animation: "pulse 1.5s infinite", flexShrink: 0 }} />
              <span style={{ fontSize: 13.5, fontWeight: 700, color: "#374151" }}>Our clients achieve: </span>
              <span style={{ fontSize: 13.5, fontWeight: 800, color: "#3B82F6" }}>{metrics[activeMetric]}</span>
            </div>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 44 }}>
              <a href="#contact" style={{
                background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff",
                padding: "15px 30px", borderRadius: 10, fontWeight: 700, fontSize: 15,
                textDecoration: "none", boxShadow: "0 6px 20px rgba(59,130,246,.4)",
                transition: "transform .2s,box-shadow .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = "0 10px 28px rgba(59,130,246,.5)"; }}
                onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = "0 6px 20px rgba(59,130,246,.4)"; }}
              >🚀 Build My POS</a>
              <a href="#case-studies" style={{
                background: "#fff", color: "#1E3A8A", padding: "15px 30px", borderRadius: 10,
                fontWeight: 700, fontSize: 15, textDecoration: "none", border: "2px solid #BFDBFE",
                transition: "all .2s", fontFamily: "'Sora',sans-serif",
              }}
                onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#93C5FD"; }}
                onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
              >📂 View Case Studies</a>
            </div>
            <div style={{ display: "flex", gap: 28, flexWrap: "wrap" }}>
              {[["180+", "POS Projects"], ["85%+", "Adoption Rate"], ["3x", "Sales Lift"], ["40%", "Admin Saved"]].map(([v, l]) => (
                <div key={l}>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 22, color: "#1E3A8A" }}>{v}</div>
                  <div style={{ fontSize: 12, color: "#9CA3AF", fontWeight: 600 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Right — POS Dashboard Visual */}
          <div style={{ position: "relative" }}>
            <div style={{ borderRadius: 24, overflow: "hidden", boxShadow: "0 40px 100px rgba(59,130,246,.18)", border: "1px solid #E5E7EB", background: "#fff" }}>
              {/* App chrome */}
              <div style={{ background: "#1E3A8A", padding: "14px 20px", display: "flex", alignItems: "center", gap: 16 }}>
                <div style={{ display: "flex", gap: 6 }}>
                  {["#EF4444","#F59E0B","#10B981"].map(c => <div key={c} style={{ width: 11, height: 11, borderRadius: "50%", background: c }} />)}
                </div>
                <div style={{ flex: 1, background: "rgba(255,255,255,.1)", borderRadius: 6, padding: "5px 12px", fontSize: 12, color: "rgba(255,255,255,.7)", fontWeight: 500 }}>
                  🔒 crm.yourcompany.com
                </div>
              </div>
              {/* Sidebar + Main */}
              <div style={{ display: "flex", minHeight: 360 }}>
                {/* Sidebar */}
                <div style={{ width: 52, background: "#F8FAFC", borderRight: "1px solid #E5E7EB", display: "flex", flexDirection: "column", alignItems: "center", padding: "16px 0", gap: 20 }}>
                  {["👥","🎯","📊","📧","⚡","⚙️"].map((icon, i) => (
                    <div key={i} style={{ width: 36, height: 36, borderRadius: 8, background: i === 1 ? "#EFF6FF" : "none", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, cursor: "pointer" }}>{icon}</div>
                  ))}
                </div>
                {/* Main panel */}
                <div style={{ flex: 1, padding: "16px", background: "#fff" }}>
                  {/* Pipeline header */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                    <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 14, color: "#0F172A" }}>Sales Pipeline</div>
                    <div style={{ fontSize: 11, background: "#EFF6FF", color: "#3B82F6", padding: "3px 10px", borderRadius: 99, fontWeight: 700 }}>Q2 2025</div>
                  </div>
                  {/* Pipeline columns */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 8 }}>
                    {[
                      { stage: "New Leads", count: 24, color: "#3B82F6", deals: ["Acme Corp — ₹4.2L", "TechStart — ₹1.8L"] },
                      { stage: "Qualified", count: 11, color: "#8B5CF6", deals: ["GlobalTech — ₹9L", "InnovateCo — ₹3.5L"] },
                      { stage: "Proposal", count: 7, color: "#F59E0B", deals: ["MegaDeal — ₹22L", "RetailGiant — ₹8L"] },
                      { stage: "Won ✓", count: 5, color: "#10B981", deals: ["SSL Group — ₹15L", "HealthCo — ₹6L"] },
                    ].map((col) => (
                      <div key={col.stage} style={{ background: "#F8FAFC", borderRadius: 10, padding: "10px 8px" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: col.color, textTransform: "uppercase", letterSpacing: ".06em" }}>{col.stage}</div>
                          <div style={{ width: 18, height: 18, borderRadius: "50%", background: col.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 9, color: "#fff", fontWeight: 800 }}>{col.count}</div>
                        </div>
                        {col.deals.map((deal, i) => (
                          <div key={i} style={{ background: "#fff", borderRadius: 6, padding: "6px 8px", marginBottom: 5, border: `1px solid ${col.color}20`, fontSize: 9.5, fontWeight: 600, color: "#374151", lineHeight: 1.4 }}>{deal}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                  {/* Bottom stats */}
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 8, marginTop: 12 }}>
                    {[["₹2.4Cr", "Pipeline Value", "#3B82F6"], ["68%", "Win Rate", "#10B981"], ["14 days", "Avg Cycle", "#8B5CF6"]].map(([v, l, c]) => (
                      <div key={l} style={{ background: `${c}08`, border: `1px solid ${c}20`, borderRadius: 8, padding: "8px 10px", textAlign: "center" }}>
                        <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 14, color: c }}>{v}</div>
                        <div style={{ fontSize: 9, color: "#9CA3AF", fontWeight: 600, marginTop: 2 }}>{l}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* Floating cards */}
            <div style={{ position: "absolute", top: 24, left: -20, background: "#fff", borderRadius: 16, padding: "14px 18px", boxShadow: "0 12px 40px rgba(16,185,129,.2)", border: "1.5px solid #D1FAE5", animation: "float 3s ease-in-out infinite", zIndex: 10 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,#10B981,#059669)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>✓</div>
                <div>
                  <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 13, color: "#065F46" }}>Deal Won! 🎉</div>
                  <div style={{ fontSize: 11, color: "#6B7280", marginTop: 2 }}>SSL Group — ₹15 Lakhs</div>
                </div>
              </div>
            </div>
            <div style={{ position: "absolute", bottom: 60, right: -20, background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", borderRadius: 16, padding: "13px 18px", boxShadow: "0 12px 36px rgba(59,130,246,.4)", animation: "float 3.5s ease-in-out infinite .5s", zIndex: 10, textAlign: "center" }}>
              <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 20, color: "#fff", lineHeight: 1 }}>3x</div>
              <div style={{ fontSize: 11, color: "rgba(255,255,255,.8)", fontWeight: 500, marginTop: 3 }}>More Deals Closed 📈</div>
            </div>
            <div style={{ position: "absolute", bottom: 20, left: -16, background: "#fff", borderRadius: 14, padding: "11px 16px", boxShadow: "0 8px 28px rgba(0,0,0,.1)", border: "1px solid #E5E7EB", animation: "float 3.8s ease-in-out infinite .3s", zIndex: 10 }}>
              <div style={{ fontSize: 10, color: "#9CA3AF", fontWeight: 700, marginBottom: 5, textTransform: "uppercase" }}>Integrates with</div>
              <div style={{ display: "flex", gap: 6 }}>
                {["📧","📅","💬","📦","💳"].map((e, i) => <span key={i} style={{ fontSize: 16 }}>{e}</span>)}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Stats Bar ─────────────────────────────────────────────────────────────────
function StatsBar() {
  return (
    <section style={{ background: "#0F172A", padding: "52px 40px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 32, textAlign: "center" }} className="pos-stats-grid">
        {STATS.map(s => (
          <div key={s.lbl}>
            <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
            <div style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,3vw,40px)", fontWeight: 900, color: "#fff", lineHeight: 1 }}>{s.val}</div>
            <div style={{ color: "#64748B", fontSize: 13, marginTop: 6, fontWeight: 500 }}>{s.lbl}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

// ── POS Modules ───────────────────────────────────────────────────────────────
function PosModules() {
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Built-In Modules</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Everything Your Sales Team Needs</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            Every POS we build or implement comes loaded with these revenue-critical modules out of the box.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(260px,1fr))", gap: 20 }}>
          {POS_MODULES.map((m, i) => (
            <div key={m.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                padding: "28px 24px", borderRadius: 18,
                border: `2px solid ${hov === i ? m.color : "#F1F5F9"}`,
                background: hov === i ? `${m.color}08` : "#FAFAFA",
                boxShadow: hov === i ? `0 16px 48px ${m.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .28s", transform: hov === i ? "translateY(-5px)" : "none",
                cursor: "default",
              }}>
              <div style={{ fontSize: 32, marginBottom: 14 }}>{m.icon}</div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 15, color: "#0F172A", marginBottom: 6 }}>{m.title}</h3>
              <p style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6 }}>{m.desc}</p>
              <div style={{ marginTop: 14, height: 3, borderRadius: 99, background: `linear-gradient(90deg,${m.color},${m.color}40)`, transform: hov === i ? "scaleX(1)" : "scaleX(0.3)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Services Grid ─────────────────────────────────────────────────────────────
function ServicesGrid() {
  const [hov, setHov] = useState(null);
  return (
    <section id="services" style={{ padding: "96px 40px", background: "#F8FAFC" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Services</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>POS Development & Implementation Services</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 560, margin: "0 auto", lineHeight: 1.75 }}>
            From a lean startup POS to an enterprise Salesforce implementation — we deliver POS solutions at every scale.
          </p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {SERVICES.map((s, i) => (
            <div key={s.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? s.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 24px 64px ${s.color}22` : "0 2px 12px rgba(0,0,0,.04)",
                transition: "all .3s ease", transform: hov === i ? "translateY(-6px)" : "none",
              }}>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${s.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${s.color}25` }}>
                {s.icon}
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 18, fontWeight: 800, color: "#0F172A", marginBottom: 10 }}>{s.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75, marginBottom: 18 }}>{s.desc}</p>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 20 }}>
                {s.tags.map(t => (
                  <span key={t} style={{ background: `${s.color}12`, color: s.color, fontSize: 11, fontWeight: 700, padding: "3px 10px", borderRadius: 99, border: `1px solid ${s.color}25` }}>{t}</span>
                ))}
              </div>
              <div style={{ color: s.color, fontWeight: 700, fontSize: 13, opacity: hov === i ? 1 : 0, transition: "opacity .2s" }}>Learn More →</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Why Choose Us ─────────────────────────────────────────────────────────────
function WhyUs() {
  const points = [
    { icon: "🎯", title: "Built Around Your Process", desc: "We map your real sales process first — not a generic template. Every stage, trigger, and automation reflects how your best reps actually win deals.", color: "#3B82F6" },
    { icon: "📈", title: "Adoption-First Design", desc: "A POS nobody uses is worthless. We involve your sales team in UAT, design for minimal friction, and provide role-specific training to hit 85%+ adoption.", color: "#10B981" },
    { icon: "🔗", title: "Deep Integration Expertise", desc: "We've integrated POSs with SAP, Oracle, Tally, Shopify, WhatsApp, Twilio, Mailchimp, LinkedIn, and 50+ more. Your POS becomes the hub of your entire operation.", color: "#8B5CF6" },
    { icon: "🤖", title: "AI Built In", desc: "Lead scoring, churn prediction, sentiment analysis, and GPT-powered email drafting — AI capabilities that make your sales team superhuman, not redundant.", color: "#F59E0B" },
    { icon: "🔒", title: "Enterprise Security", desc: "RBAC, field-level security, audit trails, 2FA, IP whitelisting, and AES-256 encryption. HIPAA and PCI DSS controls available for regulated industries.", color: "#EF4444" },
    { icon: "📊", title: "Revenue Intelligence", desc: "Pipeline forecasting, rep performance dashboards, win/loss analysis, and cohort reports that give leadership the visibility to make data-driven decisions.", color: "#EC4899" },
  ];
  const [hov, setHov] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#fff" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Why Techvanto</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>POS That Works. Teams That Win.</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Why 180+ sales teams trust Techvanto to design and build their POS backbone.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(340px,1fr))", gap: 24 }}>
          {points.map((p, i) => (
            <div key={p.title}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                background: "#fff", borderRadius: 20, padding: "32px 28px",
                border: `2px solid ${hov === i ? p.color : "#F1F5F9"}`,
                boxShadow: hov === i ? `0 20px 56px ${p.color}18` : "0 2px 8px rgba(0,0,0,.04)",
                transition: "all .3s", transform: hov === i ? "translateY(-4px)" : "none",
                display: "flex", gap: 20,
              }}>
              <div style={{ width: 52, height: 52, borderRadius: 14, background: `${p.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, flexShrink: 0, border: `1px solid ${p.color}20` }}>
                {p.icon}
              </div>
              <div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 17, color: "#0F172A", marginBottom: 8 }}>{p.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.7 }}>{p.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Tech Stack ────────────────────────────────────────────────────────────────
function TechStack() {
  const [active, setActive] = useState(0);
  const [hov, setHov] = useState(null);
  const cat = TECH_STACK[active];

  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Tech Stack</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Platforms & Technologies We Master</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>From Salesforce to fully custom builds — we work with every major POS platform and the technologies that power them.</p>
        </div>
        <div style={{ display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap", marginBottom: 44 }}>
          {TECH_STACK.map((t, i) => (
            <button key={t.category} onClick={() => { setActive(i); setHov(null); }} style={{
              padding: "10px 22px", borderRadius: 99, fontWeight: 700, fontSize: 13.5,
              cursor: "pointer", border: "2px solid", transition: "all .25s", fontFamily: "'Sora',sans-serif",
              background: active === i ? "linear-gradient(135deg,#1E3A8A,#3B82F6)" : "#fff",
              borderColor: active === i ? "#3B82F6" : "#E5E7EB",
              color: active === i ? "#fff" : "#6B7280",
              boxShadow: active === i ? "0 6px 20px rgba(59,130,246,.35)" : "none",
            }}>{t.category}</button>
          ))}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 18 }}>
          {cat.items.map((tech, i) => (
            <div key={tech.name}
              onMouseEnter={() => setHov(i)}
              onMouseLeave={() => setHov(null)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: 14,
                padding: "32px 20px 24px", borderRadius: 20,
                border: `2px solid ${hov === i ? tech.color : "#F1F5F9"}`,
                background: hov === i ? `${tech.color}08` : "#fff",
                boxShadow: hov === i ? `0 12px 36px ${tech.color}28` : "0 1px 4px rgba(0,0,0,.04)",
                transition: "all .28s cubic-bezier(.4,0,.2,1)",
                transform: hov === i ? "translateY(-6px) scale(1.03)" : "none",
                cursor: "default", position: "relative", overflow: "hidden",
              }}>
              <div style={{ width: 72, height: 72, display: "flex", alignItems: "center", justifyContent: "center", borderRadius: 18, background: hov === i ? `${tech.color}15` : "#F8FAFC", border: `1px solid ${hov === i ? tech.color + "30" : "#E5E7EB"}`, transition: "all .28s" }}>
                <img src={tech.logo} alt={tech.name} style={{ width: 44, height: 44, objectFit: "contain", transition: "transform .28s", transform: hov === i ? "scale(1.1)" : "scale(1)" }}
                  onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = `<span style="font-size:22px;font-weight:900;color:${tech.color}">${tech.name[0]}</span>`; }}
                />
              </div>
              <div style={{ textAlign: "center" }}>
                <div style={{ fontSize: 13.5, fontWeight: 700, color: hov === i ? tech.color : "#1E293B", fontFamily: "'Sora',sans-serif", transition: "color .25s" }}>{tech.name}</div>
                {hov === i && <div style={{ fontSize: 10, color: tech.color, fontWeight: 600, letterSpacing: ".06em", textTransform: "uppercase", marginTop: 2 }}>Expert Level</div>}
              </div>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, borderRadius: "0 0 18px 18px", background: `linear-gradient(90deg,${tech.color},${tech.color}80)`, transform: hov === i ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: "transform .3s" }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Process ───────────────────────────────────────────────────────────────────
function ProcessSection() {
  return (
    <section style={{ padding: "96px 40px", background: "linear-gradient(160deg,#EFF6FF 0%,#F0FDF4 100%)" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 72 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Our Process</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>From Sales Chaos to POS Clarity</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>A proven 6-stage process that has delivered 180+ POS projects on time and on budget.</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(300px,1fr))", gap: 24 }}>
          {PROCESS.map((step) => (
            <div key={step.n} style={{
              background: "#fff", borderRadius: 20, padding: "32px 28px",
              border: "1px solid #E5E7EB", boxShadow: "0 4px 20px rgba(0,0,0,.05)",
              position: "relative", overflow: "hidden", transition: "all .3s",
            }}
              onMouseEnter={e => { e.currentTarget.style.boxShadow = `0 20px 60px ${step.color}18`; e.currentTarget.style.borderColor = `${step.color}40`; e.currentTarget.style.transform = "translateY(-4px)"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,.05)"; e.currentTarget.style.borderColor = "#E5E7EB"; e.currentTarget.style.transform = "none"; }}
            >
              <div style={{ position: "absolute", top: 20, right: 24, fontFamily: "'Sora',sans-serif", fontSize: 56, fontWeight: 900, color: `${step.color}12`, lineHeight: 1, pointerEvents: "none" }}>{step.n}</div>
              <div style={{ width: 56, height: 56, borderRadius: 14, background: `${step.color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, marginBottom: 20, border: `1px solid ${step.color}20` }}>
                {step.icon}
              </div>
              <div style={{ marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 800, color: step.color, background: `${step.color}15`, padding: "3px 10px", borderRadius: 99 }}>STEP {step.n}</span>
              </div>
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 18, color: "#0F172A", marginBottom: 10 }}>{step.title}</h3>
              <p style={{ color: "#6B7280", fontSize: 14, lineHeight: 1.75 }}>{step.desc}</p>
              <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 3, background: `linear-gradient(90deg,${step.color},${step.color}50)` }} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Case Studies ──────────────────────────────────────────────────────────────
function CaseStudies() {
  const [hov, setHov] = useState(null);
  return (
    <section id="case-studies" style={{ padding: "96px 40px", background: "#fff" }} className="pos-section">
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 64 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Case Studies</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>POS Success Stories</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto" }}>Real businesses, real results — transformative POS implementations across industries.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 60 }}>
          {CASE_STUDIES.map((cs, idx) => (
            <div key={cs.title} style={{
              display: "grid", gridTemplateColumns: "1fr 1fr",
              gap: 52, alignItems: "center",
              direction: idx % 2 === 0 ? "ltr" : "rtl",
            }} className="pos-cs-grid">
              <div style={{ direction: "ltr", position: "relative", borderRadius: 20, overflow: "hidden", boxShadow: `0 32px 80px ${cs.color}25` }}
                onMouseEnter={() => setHov(cs.title)}
                onMouseLeave={() => setHov(null)}
              >
                <img src={cs.img} alt={cs.title} style={{ width: "100%", height: 360, objectFit: "cover", display: "block", transition: "transform .5s", transform: hov === cs.title ? "scale(1.04)" : "scale(1)" }} />
                <div style={{ position: "absolute", top: 20, left: 20, background: cs.color, color: "#fff", fontSize: 12, fontWeight: 700, padding: "5px 14px", borderRadius: 99 }}>{cs.industry}</div>
              </div>
              <div style={{ direction: "ltr" }}>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(24px,3vw,36px)", fontWeight: 900, color: "#0F172A", marginBottom: 14 }}>{cs.title}</h3>
                <p style={{ color: "#6B7280", fontSize: 16, lineHeight: 1.8, marginBottom: 24 }}>{cs.desc}</p>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 28 }}>
                  {cs.tags.map(t => (
                    <span key={t} style={{ background: `${cs.color}12`, color: cs.color, fontSize: 12, fontWeight: 700, padding: "4px 12px", borderRadius: 99, border: `1px solid ${cs.color}25` }}>{t}</span>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 28, marginBottom: 32, flexWrap: "wrap" }}>
                  {cs.metrics.map(m => (
                    <div key={m.lbl}>
                      <div style={{ fontFamily: "'Sora',sans-serif", fontWeight: 900, fontSize: 26, color: cs.color, lineHeight: 1 }}>{m.val}</div>
                      <div style={{ fontSize: 12, color: "#6B7280", fontWeight: 600, marginTop: 4 }}>{m.lbl}</div>
                    </div>
                  ))}
                </div>
                <a href="#" style={{
                  display: "inline-flex", alignItems: "center", gap: 8,
                  background: cs.color, color: "#fff", padding: "13px 26px", borderRadius: 10,
                  fontWeight: 700, fontSize: 14, textDecoration: "none",
                  boxShadow: `0 6px 20px ${cs.color}35`, transition: "transform .2s", fontFamily: "'Sora',sans-serif",
                }}
                  onMouseEnter={e => e.currentTarget.style.transform = "translateY(-2px)"}
                  onMouseLeave={e => e.currentTarget.style.transform = "none"}
                >Read Full Case Study →</a>
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 60 }}>
          <Link to="/portfolio" style={{
            background: "#fff", color: "#1E3A8A", border: "2px solid #BFDBFE",
            padding: "14px 32px", borderRadius: 10, fontWeight: 700, fontSize: 15,
            textDecoration: "none", fontFamily: "'Sora',sans-serif", transition: "all .2s", display: "inline-block",
          }}
            onMouseEnter={e => { e.currentTarget.style.background = "#EFF6FF"; e.currentTarget.style.borderColor = "#93C5FD"; }}
            onMouseLeave={e => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.borderColor = "#BFDBFE"; }}
          >View All Projects →</Link>
        </div>
      </div>
    </section>
  );
}

// ── FAQ ───────────────────────────────────────────────────────────────────────
function FAQ() {
  const [open, setOpen] = useState(null);
  return (
    <section style={{ padding: "96px 40px", background: "#F8FAFC" }} className="pos-section">
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>FAQ</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,44px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 16 }}>Frequently Asked Questions</h2>
          <p style={{ color: "#6B7280", fontSize: 16 }}>Everything you need to know before starting your POS project.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          {FAQS.map((faq, i) => (
            <div key={i} style={{
              background: "#fff", borderRadius: 16,
              border: `2px solid ${open === i ? "#3B82F6" : "#E5E7EB"}`,
              overflow: "hidden", transition: "border-color .25s",
              boxShadow: open === i ? "0 8px 32px rgba(59,130,246,.1)" : "none",
            }}>
              <button onClick={() => setOpen(open === i ? null : i)} style={{
                width: "100%", padding: "22px 28px", background: "none", border: "none",
                display: "flex", justifyContent: "space-between", alignItems: "center",
                cursor: "pointer", fontFamily: "'Sora',sans-serif",
              }}>
                <span style={{ fontWeight: 700, fontSize: 15.5, color: "#0F172A", textAlign: "left", lineHeight: 1.4, flex: 1, marginRight: 16 }}>{faq.q}</span>
                <div style={{
                  width: 32, height: 32, borderRadius: "50%",
                  background: open === i ? "#3B82F6" : "#F1F5F9",
                  display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, transition: "all .25s",
                }}>
                  <span style={{ color: open === i ? "#fff" : "#6B7280", fontSize: 18, lineHeight: 1, transition: "transform .25s", display: "block", transform: open === i ? "rotate(45deg)" : "none" }}>+</span>
                </div>
              </button>
              {open === i && (
                <div style={{ padding: "0 28px 24px", animation: "fadeIn .2s ease" }}>
                  <p style={{ color: "#6B7280", fontSize: 14.5, lineHeight: 1.8, borderTop: "1px solid #F1F5F9", paddingTop: 16 }}>{faq.a}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// ── Contact ───────────────────────────────────────────────────────────────────
function ContactCTA() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", teamsize: "", desc: "" });
  const [focused, setFocused] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleChange = e => setForm(f => ({ ...f, [e.target.name]: e.target.value }));
  const handleSubmit = e => { e.preventDefault(); setLoading(true); setTimeout(() => { setLoading(false); setSubmitted(true); }, 1600); };

  const iStyle = (n) => ({
    width: "100%", padding: "13px 16px", borderRadius: 10, fontSize: 14,
    border: `2px solid ${focused === n ? "#3B82F6" : "#E5E7EB"}`,
    background: focused === n ? "#EFF6FF" : "#FAFAFA",
    color: "#0F172A", outline: "none", fontFamily: "'Sora',sans-serif",
    transition: "all .2s", boxShadow: focused === n ? "0 0 0 4px rgba(59,130,246,.1)" : "none",
  });

  return (
    <section id="contact" style={{ padding: "96px 40px", background: "linear-gradient(160deg,#EFF6FF,#F0FDF4)" }} className="pos-section">
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 60 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#3B82F6", letterSpacing: ".1em", textTransform: "uppercase" }}>Get Started</span>
          <h2 style={{ fontFamily: "'Sora',sans-serif", fontSize: "clamp(28px,4vw,46px)", fontWeight: 900, color: "#0F172A", marginTop: 8, marginBottom: 14 }}>Let's Build Your POS System</h2>
          <p style={{ color: "#6B7280", fontSize: 16, maxWidth: 520, margin: "0 auto", lineHeight: 1.75 }}>Tell us about your sales team and process — get a free POS audit + detailed proposal within 24 hours.</p>
        </div>
        <div className="pos-contact-grid">
          {/* Info panel */}
          <div>
            <div style={{ background: "linear-gradient(135deg,#1E3A8A,#2563EB,#3B82F6)", borderRadius: 24, padding: "40px 36px", marginBottom: 24, boxShadow: "0 20px 60px rgba(59,130,246,.3)", position: "relative", overflow: "hidden" }}>
              <div style={{ position: "absolute", top: -40, right: -40, width: 200, height: 200, borderRadius: "50%", background: "rgba(255,255,255,.06)" }} />
              <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#fff", marginBottom: 8, position: "relative" }}>Free POS Audit Included</h3>
              <p style={{ color: "rgba(255,255,255,.75)", fontSize: 14, lineHeight: 1.7, marginBottom: 32, position: "relative" }}>We'll audit your current sales process and tools — then recommend the exact POS architecture to 3x your team's output.</p>
              {[
                { icon: "📞", label: "Phone", val: "+91 96462 06032" },
                { icon: "✉️", label: "Email", val: "info@techvanto.com" },
                { icon: "📍", label: "Location", val: "New Delhi, India 🇮🇳" },
                { icon: "⏰", label: "Hours", val: "Mon–Sat, 9AM – 7PM IST" },
              ].map(item => (
                <div key={item.label} style={{ display: "flex", gap: 14, marginBottom: 18, alignItems: "flex-start" }}>
                  <div style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.15)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>{item.icon}</div>
                  <div>
                    <div style={{ fontSize: 10, color: "rgba(255,255,255,.5)", fontWeight: 700, textTransform: "uppercase", letterSpacing: ".06em", marginBottom: 2 }}>{item.label}</div>
                    <div style={{ fontSize: 13.5, color: "#fff", fontWeight: 600 }}>{item.val}</div>
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: "#fff", borderRadius: 20, padding: "28px", border: "1px solid #E5E7EB", boxShadow: "0 4px 16px rgba(0,0,0,.05)" }}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 15, color: "#0F172A", marginBottom: 18 }}>What You Get — Free</h4>
              {[
                ["🔍", "Current sales process & tools audit"],
                ["🎯", "POS platform recommendation (Custom / Salesforce / Zoho)"],
                ["💰", "Transparent fixed-price quote"],
                ["🔒", "NDA signed before any discussion"],
                ["📊", "ROI projection for your team size"],
              ].map(([icon, text]) => (
                <div key={text} style={{ display: "flex", gap: 10, alignItems: "flex-start", marginBottom: 12 }}>
                  <span style={{ fontSize: 16, flexShrink: 0, marginTop: 1 }}>{icon}</span>
                  <span style={{ fontSize: 13.5, color: "#374151", fontWeight: 500, lineHeight: 1.5 }}>{text}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div style={{ background: "#fff", borderRadius: 24, padding: "44px", boxShadow: "0 8px 48px rgba(0,0,0,.08)", border: "1px solid #F1F5F9" }}>
            {submitted ? (
              <div style={{ textAlign: "center", padding: "40px 0" }}>
                <div style={{ width: 88, height: 88, borderRadius: "50%", background: "linear-gradient(135deg,#3B82F6,#1E3A8A)", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 40, boxShadow: "0 12px 36px rgba(59,130,246,.35)" }}>🤝</div>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontSize: 24, fontWeight: 800, color: "#0F172A", marginBottom: 12 }}>Request Received! 🎉</h3>
                <p style={{ color: "#6B7280", fontSize: 15, lineHeight: 1.7, maxWidth: 320, margin: "0 auto 28px" }}>Our POS specialists will review your requirements and get back to you within 24 hours with a free audit and proposal.</p>
                <button onClick={() => { setSubmitted(false); setForm({ name: "", email: "", phone: "", teamsize: "", desc: "" }); }} style={{ background: "linear-gradient(135deg,#1E3A8A,#3B82F6)", color: "#fff", border: "none", padding: "13px 28px", borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}>Submit Another Request</button>
              </div>
            ) : (
              <>
                <h3 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 800, fontSize: 22, color: "#0F172A", marginBottom: 6 }}>Tell Us About Your Sales Team</h3>
                <p style={{ color: "#9CA3AF", fontSize: 13.5, marginBottom: 32 }}>The more context you provide, the better our POS recommendation will be.</p>
                <form onSubmit={handleSubmit}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="pos-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Full Name *</label>
                      <input name="name" value={form.name} onChange={handleChange} onFocus={() => setFocused("name")} onBlur={() => setFocused(null)} placeholder="Your name" required style={iStyle("name")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Email *</label>
                      <input type="email" name="email" value={form.email} onChange={handleChange} onFocus={() => setFocused("email")} onBlur={() => setFocused(null)} placeholder="name@company.com" required style={iStyle("email")} />
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }} className="pos-form-row">
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Phone *</label>
                      <input type="tel" name="phone" value={form.phone} onChange={handleChange} onFocus={() => setFocused("phone")} onBlur={() => setFocused(null)} placeholder="+91 XXXXX XXXXX" required style={iStyle("phone")} />
                    </div>
                    <div>
                      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Sales Team Size</label>
                      <select name="teamsize" value={form.teamsize} onChange={handleChange} onFocus={() => setFocused("teamsize")} onBlur={() => setFocused(null)} style={{ ...iStyle("teamsize"), appearance: "none", cursor: "pointer" }}>
                        <option value="">Select team size…</option>
                        {["1–5 reps", "6–20 reps", "21–50 reps", "51–100 reps", "100+ reps"].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div style={{ marginBottom: 28 }}>
                    <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#374151", marginBottom: 6, textTransform: "uppercase", letterSpacing: ".05em" }}>Describe Your POS Needs *</label>
                    <textarea name="desc" value={form.desc} onChange={handleChange} onFocus={() => setFocused("desc")} onBlur={() => setFocused(null)} placeholder="What are your biggest sales challenges? What tools do you currently use? Do you need a custom build or platform implementation? What integrations matter most (ERP, WhatsApp, marketing tools)?" rows={5} required style={{ ...iStyle("desc"), resize: "vertical", minHeight: 130, lineHeight: 1.7 }} />
                  </div>
                  <p style={{ fontSize: 12, color: "#9CA3AF", marginBottom: 20 }}>🔒 Your information is 100% secure and kept strictly confidential.</p>
                  <button type="submit" disabled={loading} style={{
                    width: "100%", padding: "15px", borderRadius: 12,
                    background: loading ? "#93C5FD" : "linear-gradient(135deg,#1E3A8A,#3B82F6)",
                    color: "#fff", border: "none", cursor: loading ? "not-allowed" : "pointer",
                    fontWeight: 800, fontSize: 16, fontFamily: "'Sora',sans-serif",
                    boxShadow: loading ? "none" : "0 8px 24px rgba(59,130,246,.4)",
                    transition: "all .25s", display: "flex", alignItems: "center", justifyContent: "center", gap: 10,
                  }}>
                    {loading ? "Submitting…" : "🤝 Get Free POS Audit & Proposal"}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ── Footer ────────────────────────────────────────────────────────────────────
function Footer() {
  const cols = [
    { title: "Services", links: ["Web Development", "Mobile Apps", "AI & ML", "Cloud & DevOps", "UI/UX Design", "E-Commerce"] },
    { title: "Solutions", links: ["POS Solution", "ERP Solution", "HRM Solution", "LMS Solution", "POS Solution", "SaaS Platform"] },
    { title: "Technologies", links: ["React & Next.js", "Flutter & RN", "Node.js & Laravel", "AWS & GCP", "Docker & K8s", "Python & AI"] },
  ];
  return (
    <footer style={{ background: "#0F172A", color: "#fff", padding: "72px 40px 32px" }}>
      <div style={{ maxWidth: 1280, margin: "0 auto" }}>
        <div className="footer-grid" style={{ marginBottom: 56 }}>
          <div>
            <div style={{ marginBottom: 16 }}>
              <img src={LOGO_SRC} alt="Techvanto" style={{ height: 140, width: "auto", objectFit: "contain", filter: "brightness(0) invert(1)", display: "block" }} />
            </div>
            <p style={{ color: "#94A3B8", lineHeight: 1.75, fontSize: 14, maxWidth: 300, marginBottom: 24 }}>
              Building world-class digital products for businesses across 20+ countries. Innovation. Scale. Excellence.
            </p>
            <div style={{ display: "flex", gap: 12 }}>
              {["𝕏", "in", "𝔾", "▶"].map((icon, i) => (
                <a key={i} href="#" style={{ width: 38, height: 38, borderRadius: 10, background: "rgba(255,255,255,.08)", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", textDecoration: "none", fontSize: 14, fontWeight: 700, transition: "all .2s" }}
                  onMouseEnter={e => { e.currentTarget.style.background = "#3B82F6"; e.currentTarget.style.color = "#fff"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,.08)"; e.currentTarget.style.color = "#94A3B8"; }}
                >{icon}</a>
              ))}
            </div>
          </div>
          {cols.map(col => (
            <div key={col.title}>
              <h4 style={{ fontFamily: "'Sora',sans-serif", fontWeight: 700, fontSize: 14, color: "#fff", marginBottom: 20, letterSpacing: ".04em" }}>{col.title}</h4>
              {col.links.map(l => (
                <a key={l} href="#" style={{ display: "block", color: "#64748B", fontSize: 13.5, textDecoration: "none", marginBottom: 10, transition: "color .15s" }}
                  onMouseEnter={e => e.target.style.color = "#93C5FD"}
                  onMouseLeave={e => e.target.style.color = "#64748B"}
                >{l}</a>
              ))}
            </div>
          ))}
        </div>
        <div style={{ borderTop: "1px solid #1E293B", paddingTop: 28, display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
          <p style={{ color: "#475569", fontSize: 13 }}>© 2025 Techvanto Private Limited. All rights reserved.</p>
          <p style={{ color: "#475569", fontSize: 13 }}>Made with ❤️ in India 🇮🇳</p>
        </div>
      </div>
    </footer>
  );
}

// ── Root ──────────────────────────────────────────────────────────────────────
export default function PosSolutions() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;500;600;700;800;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { scroll-behavior: smooth; }
        body { font-family: 'Sora', -apple-system, sans-serif; background: #fff; color: #0F172A; overflow-x: hidden; }

        @keyframes fadeIn { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:none; } }
        @keyframes float  { 0%,100% { transform:translateY(0); } 50% { transform:translateY(-10px); } }
        @keyframes pulse  { 0%,100% { opacity:1; } 50% { opacity:.4; } }

        .nav-links          { display:flex; align-items:center; gap:4px; }
        .nav-cta            { display:flex !important; }
        .mob-menu-btn       { display:none; }

        .pos-hero-grid      { display:grid; grid-template-columns:1fr 1fr; gap:64px; align-items:center; }
        .pos-stats-grid     { display:grid; grid-template-columns:repeat(4,1fr); gap:32px; }
        .pos-cs-grid        { display:grid; grid-template-columns:1fr 1fr; gap:52px; align-items:center; }
        .pos-contact-grid   { display:grid; grid-template-columns:1fr 1.5fr; gap:48px; align-items:start; }
        .footer-grid        { display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:48px; }
        .pos-form-row       { display:grid; grid-template-columns:1fr 1fr; gap:16px; }

        @media (max-width:1100px) {
          .pos-hero-grid  { gap:40px; }
          .footer-grid    { grid-template-columns:1fr 1fr; gap:32px; }
          .pos-stats-grid { grid-template-columns:repeat(2,1fr); }
        }
        @media (max-width:900px) {
          .pos-hero-grid    { grid-template-columns:1fr; gap:48px; }
          .pos-cs-grid      { grid-template-columns:1fr !important; gap:36px !important; direction:ltr !important; }
          .pos-contact-grid { grid-template-columns:1fr; gap:32px; }
          .footer-grid      { grid-template-columns:1fr 1fr; gap:32px; }
          .nav-links        { display:none !important; }
          .nav-cta          { display:none !important; }
          .mob-menu-btn     { display:flex !important; }
        }
        @media (max-width:640px) {
          .footer-grid    { grid-template-columns:1fr; }
          .pos-stats-grid { grid-template-columns:repeat(2,1fr); gap:24px; }
          .pos-form-row   { grid-template-columns:1fr !important; }
        }
        @media (max-width:500px) { .topbar-right { display:none !important; } }
        @media (max-width:900px) { .pos-section { padding-left:20px !important; padding-right:20px !important; } }
        @media (max-width:640px) { .pos-section { padding-left:16px !important; padding-right:16px !important; padding-top:64px !important; padding-bottom:64px !important; } }
      `}</style>

      <Hero />
      <StatsBar />
      <PosModules />
      <ServicesGrid />
      <WhyUs />
      <TechStack />
      <ProcessSection />
      <CaseStudies />
      <FAQ />
    </>
  );
}